import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  effect,
  ElementRef,
  input,
  InputSignalWithTransform,
  model,
  output,
  OutputEmitterRef,
  QueryList,
  signal,
  TemplateRef,
  viewChild,
  viewChildren,
  WritableSignal,
} from '@angular/core';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { KeyValuePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { SelectionModel } from '@angular/cdk/collections';

import { DynamicTitleDirective, TypeSafeMatCellDefDirective } from '@rp/shared/directives';
import { TranslateModule } from '@ngx-translate/core';
import { PaginationRequest } from '@rp/shared/models';
import { ObjectUtils } from '@rp/utils';
import { filter, isEmpty } from 'lodash';

import { TableColumn } from './models/table-column.interface';
import { IconComponent } from '../icon';
import { TableAction } from './models/table-action.enum';
import { TableRowAction } from './models/table-row-action.interface';
import { CellComponent } from './components/cell.component';
import { RowDirective } from './directives/row.directive';
import { CustomMatPaginatorIntl } from './intl/custom-paginator.intl';
import { TablePlaceholderComponent } from '../table-placeholder';
import { TableColumnGroup } from './models/table-column-group.interface';
import { TableCheckboxComponent } from './components/table-checkbox/table-checkbox.component';

@Component({
  selector: 'rp-table',
  standalone: true,
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TablePlaceholderComponent,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    TypeSafeMatCellDefDirective,
    TranslateModule,
    NgTemplateOutlet,
    IconComponent,
    MatSort,
    MatSortHeader,
    MatPaginator,
    DynamicTitleDirective,
    NgClass,
    KeyValuePipe,
    TableCheckboxComponent,
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'rp-paginator-select' },
    },
  ],
})
/* eslint-disable  @typescript-eslint/no-explicit-any */
export class TableComponent<T, G extends string = any> implements AfterViewInit {
  @ContentChildren(RowDirective) set itemsContents(contents: QueryList<RowDirective<T>>) {
    this.dataSource.data = contents?.map(content => content.item());

    contents.forEach((content: RowDirective<T>, rowIndex: number) => {
      const cells = content.cells?.toArray();
      cells?.forEach((cell: CellComponent) => {
        this.rowCellsTemplatesMap[cell.cellDef() + '-' + rowIndex] = cell.cellTemplate();
      });
    });
  }

  readonly sortRef = viewChild(MatSort);
  readonly headerCells = viewChildren('headerCell', { read: ElementRef });

  //* Inputs *//
  readonly columns = input.required<TableColumn[]>();
  readonly columnGroups = input<Record<G, TableColumnGroup>>();
  readonly itemsLength = input.required<number>();
  readonly pageSizeOptions = input<number[]>([10, 20, 50]);
  readonly defaultPageSize = model<number>(10);
  readonly selection = input<SelectionModel<T>>();
  readonly showFirstLastButtons = input<boolean, boolean>(true, {
    transform: booleanAttribute,
  });
  readonly isHoverEnabled = input(false, {
    transform: booleanAttribute,
  });
  readonly isShowTotal = input(false, {
    transform: booleanAttribute,
  });
  readonly isCheckboxAllSelected = input(false, {
    transform: booleanAttribute,
  });
  readonly isLoading = input(false, {
    transform: booleanAttribute,
  });
  readonly titlePlaceholder: InputSignalWithTransform<string, string> = input<string, string>(
    'general.placeholders.noItemsPlaceholderTitle',
    {
      transform: (value: string) => value || 'general.placeholders.noItemsPlaceholderTitle',
    },
  );
  readonly subtitlePlaceholder: InputSignalWithTransform<string, string> = input<string, string>(
    'general.placeholders.noItemsPlaceholderSubTitle',
    {
      transform: (value: string) => value || 'general.placeholders.noItemsPlaceholderSubTitle',
    },
  );

  readonly rowCellsTemplatesMap: Record<string, TemplateRef<HTMLElement>> = {};

  //* Signals *//
  readonly displayedColumns: WritableSignal<string[]> = signal<string[]>([]);
  readonly displayedColumnGroups: WritableSignal<string[]> = signal<string[]>([]);
  readonly pageSize: WritableSignal<number> = signal<number>(this.defaultPageSize());
  readonly pageIndex: WritableSignal<number> = signal<number>(0);

  // * Outputs *//
  readonly action: OutputEmitterRef<TableRowAction<T>> = output<TableRowAction<T>>();
  readonly sort: OutputEmitterRef<Sort> = output<Sort>();
  readonly pagination = output<PaginationRequest>();
  readonly rowClick = output<T>();
  readonly isCheckboxAllSelectedChange = output<boolean>();

  // * Mat Table *//
  readonly dataSource = new MatTableDataSource<T>([]);

  // * Column Group *//
  groupObservers: ResizeObserver[] = [];
  displayedColumnGroupsSet = new Set<string>();

  constructor() {
    effect(
      () => {
        this.displayedColumns.set([
          ...this.columns()
            .filter(({ hidden }) => !hidden || !hidden())
            .map(({ columnDef }) => columnDef),
        ]);
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      if (this.sortRef()) {
        this.columns().forEach((column: TableColumn) => {
          if (column.sortActive) {
            this.sortRef()?.sort({
              id: column.columnDef,
              start: column.sortDirection || 'asc',
              disableClear: false,
            });
          }
        });
      }
    });

    effect(() => {
      if (this.columnGroups() && !this.isLoading()) {
        this._resetColumnGroupObservers();

        this.headerCells().forEach((headerCell: ElementRef<HTMLElement>) => {
          const element = headerCell.nativeElement;
          const columnGroupBy = element.getAttribute('group-by') as G;

          const observer = new ResizeObserver(() => {
            const isColumnGroupPresent = this.displayedColumnGroupsSet.has(columnGroupBy);
            const columnGroup = this.columnGroups()[columnGroupBy];
            const groupRelatedHeaderCells = document.querySelectorAll(
              `.table__header[group-by="${columnGroupBy}"]`,
            );

            if (groupRelatedHeaderCells?.length) {
              const groupColumnWidth = this._calculateColumnGroupWidth(groupRelatedHeaderCells);
              const isColumnGroupWidthChanged = columnGroup.width !== groupColumnWidth;

              if (!isColumnGroupPresent || isColumnGroupWidthChanged) {
                this._setColumnGroups({ columnGroup, columnGroupBy, groupColumnWidth });
              }
            } else if (isColumnGroupPresent) {
              this._resetColumnGroups(columnGroup);
            }
          });

          observer.observe(element);

          this.groupObservers.push(observer);
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sortRef();
    this.pageSize.set(this.defaultPageSize());
  }

  onPageChange(pageEvent: PageEvent): void {
    this.pageSize.set(pageEvent.pageSize);
    this.pageIndex.set(pageEvent.pageIndex);
    this.pagination.emit({
      page: pageEvent.pageIndex + 1,
      limit: pageEvent.pageSize,
    });
  }

  onAction(event: Event, action: TableAction, item: T): void {
    event.stopPropagation();
    this.action.emit({ action, item });
  }

  onRowClick(item: T): void {
    this.rowClick.emit(item);
  }

  sortData(sort: Sort): void {
    this.sort.emit(sort);
  }

  setDefaultPageSize(pageSize: number): void {
    this.defaultPageSize.set(pageSize);
  }

  setPaginator(pageIndex: number, pageSize: number): void {
    this.pageIndex.set(pageIndex);
    this.pageSize.set(pageSize);
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection().clear();
      this.isCheckboxAllSelectedChange.emit(false);

      return;
    }

    this.isCheckboxAllSelectedChange.emit(true);
    this.selection().select(...this.dataSource.data.filter(x => !ObjectUtils.isEmptyObject(x)));
  }

  isAllSelected() {
    const numSelected = this.selection().selected.length;
    const numRows = filter(this.dataSource.data, obj => !isEmpty(obj)).length;

    return numSelected === numRows;
  }

  private _calculateColumnGroupWidth(groupRelatedHeaderCells: NodeListOf<Element>): number {
    return Array.from(groupRelatedHeaderCells).reduce(
      (width, headerCell) => width + headerCell.getBoundingClientRect().width,
      0,
    );
  }

  private _setColumnGroups(options: {
    columnGroup: TableColumnGroup;
    columnGroupBy: G;
    groupColumnWidth: number;
  }): void {
    options.columnGroup.isShown = true;
    options.columnGroup.width = options.groupColumnWidth;

    this.displayedColumnGroupsSet.add(options.columnGroupBy);

    this.displayedColumnGroups.set(Array.from(this.displayedColumnGroupsSet));
  }

  private _resetColumnGroupObservers(): void {
    if (this.groupObservers.length) {
      this.groupObservers.forEach(observer => observer.disconnect());
      this.groupObservers = [];
    }
  }

  private _resetColumnGroups(columnGroup: TableColumnGroup): void {
    if (this.displayedColumnGroupsSet.size) {
      columnGroup.isShown = false;
      columnGroup.width = 0;

      this.displayedColumnGroupsSet.clear();

      this.displayedColumnGroups.set([]);
    }
  }
}
